// Lib
import React, { useCallback, useState, useLayoutEffect, useRef } from 'react';
import 'cropperjs/dist/cropper.css';
import Cropper from 'cropperjs';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { inRange } from 'lodash/fp';

// Utils
import { getHeight, getWidth } from '../../../../../common/maths/geometry/rect';
import { getElementModalSize } from '../../../modal/elementModalUtils';
import {
    getCaption,
    getElementId,
    getHasDrawing,
    getShowCaption,
} from '../../../../../common/elements/utils/elementPropertyUtils';
import {
    getLargestImageSize,
    getOriginalImage,
    getImageEditorDataRotation,
    getOriginalImageWidth,
    getOriginalImageHeight,
} from '../imageModalUtils';
import { getImageSource } from '../../imageHelper';
import { appendToFilename, getFilenameFromUrl } from '../imageModalFileUtils';
import { fitSizeWithinBounds } from '../imageModalSizingUtils';

// Actions
import { updateElement } from '../../../actions/elementActions';

// Selectors
import { getIsFeatureEnabledForCurrentUser } from '../../../feature/elementFeatureSelector';
import { getElement } from '../../../selectors/elementSelector';

// Components
import Icon from '../../../../components/icons/Icon';
import Button from '../../../../components/buttons/Button';
import Spinner from '../../../../components/loaders/Spinner';
import ElementImage from '../../../../components/images/ElementImage';
import Caption from '../../../../components/caption/Caption';
import { SimpleDrawingSvg } from '../../../drawing/DrawingSvg';
import ImageModalToolbarSecondaryTools from '../toolbar/ImageModalToolbarSecondaryTools';
import ImageModalV2BackgroundToolbar from '../v2/toolbar/ImageModalV2BackgroundToolbar';
import ImageModalV2EditorToolbar from '../v2/toolbar/ImageModalV2EditorToolbar';
import type { ImMNElement, MNImageContent } from '../../../../../common/elements/elementModelTypes';

// Constants
import { IMAGE_MODAL_FOOTER_SIZE } from '../imageModalConstants';
import { IMAGE_TYPES } from '../../../../../common/media/mediaConstants';
import { ExperimentId } from '../../../../../common/experiments/experimentsConstants';

// Styles
import './ImageModalEditor.scss';

// Hooks
import { useImageRotation } from './hooks/useImageRotation';
import { useCropSaveHandler } from './hooks/useCropSaveHandler';
import {
    IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY,
    useImageModalEditorImageLoader,
} from './hooks/useImageModalEditorImageLoader';
import { useBackgroundRemovalWipeOverlay } from './hooks/useBackgroundRemovalWipeOverlay';
import { useBackgroundRemovalCrop } from '../v2/content/hooks/useBackgroundRemovalCrop';
import { adjustCropperForRotation } from './utils/cropperUtils';
import { useTranslator } from '../../../../components/i18n/useTranslator';
import type { BackgroundRemovalProgress } from '../v2/toolbar/imageModalBackgroundRemovalProgressTypes';
import {
    buildBackgroundRemovalToggleChanges,
    isBackgroundRemovalActive,
} from '../v2/toolbar/imageModalBackgroundRemovalUtils';
import type { GenericReduxDispatch } from '../../../../../common/actions/actionTypes';

/**
 * Resolve the full-source image used consistently for editor sizing, loading, and crop persistence.
 */
const getCropSourceImage = (element: ImMNElement, backgroundRemovalEditSource?: MNImageContent): MNImageContent =>
    backgroundRemovalEditSource || getOriginalImage(element);

export type ImageModalEditorProps = {
    element: ImMNElement;
    gridSize: number;
    attachment: unknown;
    windowWidth: number;
    windowHeight: number;
    idealHeight: number;
    // The available area the editor fits the source image into (V2). When absent (V1) the editor
    // derives its size from the window via getElementModalSize.
    editorBounds?: {
        width: number;
        height: number;
    };
    cancelEditing: () => void;
    afterSaveCb: () => void;
    toolbarVariant?: 'v1' | 'v2';
    toolbarMode?: 'edit' | 'background';
};

type ImageModalEditorContentProps = ImageModalEditorProps & {
    editorIsReady: boolean;
    isV2Toolbar: boolean;
    toolbarMode?: 'edit' | 'background';
    showImageReplacementWipe: boolean;
    showEditorLoadingPreview: boolean;
    toolbarClassName: string;
    backgroundRemovalProgress: BackgroundRemovalProgress | null;
    backgroundRemovalControlsDisabled?: boolean;
    // Whether the removed-background variant is staged to be applied on save. The `element` passed in already
    // reflects this variant (so the editor previews it), but it is only persisted when the user saves.
    backgroundRemovalStagedActive: boolean;
    onChangeBackgroundRemovalStagedActive: (active: boolean) => void;
    toast?: React.ReactNode;
    onEditorReadyChange: (ready: boolean) => void;
    setBackgroundRemovalProgress: (progress: BackgroundRemovalProgress | null) => void;
};

export const ImageModalEditorContent: React.FC<ImageModalEditorContentProps> = (props) => {
    const {
        element,
        gridSize,
        attachment,
        windowWidth,
        windowHeight,
        idealHeight,
        editorBounds,
        cancelEditing,
        afterSaveCb,
    } = props;

    const translate = useTranslator('popup', 'imageModal.editor');
    const useSecureMediaUrl = true;

    const renderSecureImagesThroughCF = useSelector(
        getIsFeatureEnabledForCurrentUser(ExperimentId.renderSecureImagesThroughCF),
    );

    const {
        backgroundRemovalEditSource,
        imageType,
        uploadEditedImage,
        createAdditionalCroppedImageBlob,
        uploadAdditionalCroppedImage,
        updateElementImageData,
        revertToOriginalImageData,
        getRevertImage,
    } = useBackgroundRemovalCrop({
        element,
        elementId: getElementId(element),
        useSecureMediaUrl,
        renderSecureImagesThroughCF,
    });

    // Background removal edits must load from the canonical source variant for the active side.
    // Otherwise repeated crops would keep re-cropping the already-edited visible image.
    const cropSourceImage = getCropSourceImage(element, backgroundRemovalEditSource);
    const {
        backgroundRemovalControlsDisabled,
        backgroundRemovalProgress,
        backgroundRemovalStagedActive,
        onChangeBackgroundRemovalStagedActive,
        editorIsReady,
        isV2Toolbar,
        toolbarMode,
        onEditorReadyChange,
        setBackgroundRemovalProgress,
        showEditorLoadingPreview,
        showImageReplacementWipe,
        toolbarClassName,
        toast,
    } = props;

    const dispatch = useDispatch<GenericReduxDispatch>();

    const [initialRotation] = useState(getImageEditorDataRotation(element) || 0);
    const [currentRotation, setCurrentRotation] = useState(initialRotation);
    const [hideDrawing, setHideDrawing] = useState(false);
    const [showWarningDialog, setShowWarningDialog] = useState(false);
    const [isCropToolSelected, setIsCropToolSelected] = useState(!isV2Toolbar);

    const imageRef = useRef<HTMLImageElement | null>(null);
    const cropperRef = useRef<Cropper | null>(null);
    const isDirtyRef = useRef(false);
    const initialCropDataRef = useRef<Cropper.CropBoxData | null>();

    const elementId = getElementId(element);
    const hasDrawing = getHasDrawing(element);
    const caption = getShowCaption(element) && getCaption(element);

    const originalImageWidth = getWidth(cropSourceImage) || getOriginalImageWidth({ element, gridSize, attachment });
    const originalImageHeight = getHeight(cropSourceImage) || getOriginalImageHeight({ element, gridSize, attachment });

    const isRotatedOnSide = !!currentRotation && currentRotation !== -180;

    // Flip the image width and height if it's rotated onto its side
    const imageWidth = isRotatedOnSide ? originalImageHeight : originalImageWidth;
    const imageHeight = isRotatedOnSide ? originalImageWidth : originalImageHeight;

    let modalWidth: number;
    let modalHeight: number;
    if (editorBounds) {
        const fittedEditorImage = fitSizeWithinBounds({ width: imageWidth, height: imageHeight }, editorBounds);
        modalWidth = fittedEditorImage.width;
        modalHeight = fittedEditorImage.height;
    } else {
        const calculatedModalSize = getElementModalSize({
            windowWidth,
            windowHeight,
            imageWidth,
            imageHeight,
            idealHeight,
            caption,
            footerSize: IMAGE_MODAL_FOOTER_SIZE,
            modalMinWidth: 0,
            modalMinHeight: 0,
            gridSize,
            modalVerticalMarginGU: 5,
        });
        modalWidth = calculatedModalSize.modalWidth;
        modalHeight = calculatedModalSize.modalHeight;
    }

    const svgWrapperStyle = {
        width: '100%',
        height: '100%',
    };

    const loadingImageStyles = {
        width: isRotatedOnSide ? modalHeight : modalWidth,
        height: isRotatedOnSide ? modalWidth : modalHeight,
        transform: `rotate(${initialRotation}deg)`,
    };

    const imageSize = getLargestImageSize(cropSourceImage);
    const largestUneditedImageUrl = getImageSource({
        imageDetails: cropSourceImage,
        imageSize,
        elementId,
        useSecureMediaUrl,
        renderSecureImagesThroughCF,
    });

    const { handleRotateImage } = useImageRotation({
        cropperRef,
        isDirtyRef,
        initialCropDataRef,
        currentRotation,
        initialRotation,
        setCurrentRotation,
        setHideDrawing,
    });

    // Apply the staged background-removal variant once crop/rotate persistence has run, reading the element
    // fresh so it reflects any cropped variants produced by the save above. We re-apply even when the active
    // variant already matches the staged one (e.g. a crop already moved it), because the toggle also clears the
    // element background/showBackground for the transparent variant — mirroring the original immediate flow.
    const commitStagedBackgroundRemoval = useCallback(() => {
        dispatch((dispatchThunk, getState) => {
            const freshElement = getElement(getState(), { elementId });
            if (!freshElement) return;

            // Nothing to apply when the original (background-shown) variant is already active.
            if (!backgroundRemovalStagedActive && !isBackgroundRemovalActive(freshElement)) return;

            const changes = buildBackgroundRemovalToggleChanges({
                element: freshElement,
                active: backgroundRemovalStagedActive,
            });
            if (!changes) return;

            dispatchThunk(updateElement({ id: elementId, changes, silent: true, sync: true }));
        });
    }, [dispatch, elementId, backgroundRemovalStagedActive]);

    const handleAfterSave = useCallback(() => {
        commitStagedBackgroundRemoval();
        afterSaveCb();
    }, [commitStagedBackgroundRemoval, afterSaveCb]);

    const { handleSave } = useCropSaveHandler({
        cropperRef,
        isDirtyRef,
        elementId,
        cropSourceImage,
        imageType,
        largestUneditedImageUrl,
        hideDrawing,
        getFilenameFromUrl,
        appendToFilename,
        inRange,
        updateElementImageData,
        revertToOriginalImageData,
        uploadEditedImage,
        getRevertedImage: getRevertImage,
        createAdditionalCroppedImageBlob,
        uploadAdditionalCroppedImage,
        afterSaveCb: handleAfterSave,
    });

    useLayoutEffect(() => {
        setCurrentRotation(initialRotation);
        setHideDrawing(false);
        setShowWarningDialog(false);
        onEditorReadyChange(false);
        isDirtyRef.current = false;
        initialCropDataRef.current = null;
    }, [elementId, imageType, initialRotation, largestUneditedImageUrl, onEditorReadyChange]);

    const editorImageLoadKey = `${elementId}-${imageType}-${initialRotation}-${largestUneditedImageUrl || ''}`;
    const { handleImageDataUrlLoad, handleWipeAnimationEnd, wipeOverlay } = useBackgroundRemovalWipeOverlay({
        enabled: showImageReplacementWipe,
        editorImageLoadKey,
        editorIsReady,
    });

    useImageModalEditorImageLoader({
        imageRef,
        cropperRef,
        isDirtyRef,
        element,
        imageType,
        loadKey: editorImageLoadKey,
        loadStrategy: isV2Toolbar
            ? IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY.KEEP_PREVIOUS_IMAGE_UNTIL_READY
            : IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY.CLEAR_IMAGE_BEFORE_LOAD,
        sourceUrl: largestUneditedImageUrl,
        onEditorReadyChange,
        onImageDataUrlLoad: handleImageDataUrlLoad,
    });

    useLayoutEffect(() => {
        const cropper = cropperRef.current;
        if (!editorIsReady || !cropper) return;
        if (!initialCropDataRef.current) return;

        adjustCropperForRotation(cropper, modalWidth, initialCropDataRef.current);
    }, [currentRotation, editorIsReady, modalWidth]);

    const handleRotateClick = () => {
        if (currentRotation === initialRotation && hasDrawing) {
            setShowWarningDialog(true);
            return;
        }

        handleRotateImage();
    };

    const handleConfirmRotation = () => {
        setShowWarningDialog(false);
        handleRotateImage();
    };

    const handleCancelRotation = () => {
        setShowWarningDialog(false);
    };

    const toggleCropTool = () => {
        setIsCropToolSelected((isSelected) => !isSelected);
    };

    const toolbarDisabled = !editorIsReady || showWarningDialog || !!backgroundRemovalControlsDisabled;

    return (
        <>
            <div className="image-modal-content">
                <div className="image-container">
                    <div className="ImageEditor">
                        <div
                            className={classNames('editor', {
                                loading: showEditorLoadingPreview,
                                'crop-tool-disabled': !isCropToolSelected,
                            })}
                            style={{ width: modalWidth, height: modalHeight }}
                        >
                            {showEditorLoadingPreview && (
                                <div className="loading-message">
                                    <div className="loading-image" style={loadingImageStyles}>
                                        <ElementImage
                                            useSecureMediaUrl={useSecureMediaUrl}
                                            showBrokenIconOnError
                                            element={element}
                                            imageData={cropSourceImage}
                                            imageType={IMAGE_TYPES.ELEMENT}
                                            widthPx={modalWidth}
                                            key={`${elementId}-${largestUneditedImageUrl}`}
                                        />
                                    </div>
                                    <SimpleDrawingSvg element={element} />
                                    {!isV2Toolbar && (
                                        <div className="loading-overlay">
                                            <Spinner show />
                                            {translate('loadingEditor')}…
                                        </div>
                                    )}
                                </div>
                            )}

                            <img
                                ref={(c) => {
                                    imageRef.current = c;
                                }}
                                className="editor-source-image"
                                style={{ width: modalWidth, height: modalHeight }}
                            />
                            {wipeOverlay && (
                                <div
                                    key={wipeOverlay.renderKey}
                                    className={classNames('bg-removal-wipe-overlay', {
                                        'is-wiping': wipeOverlay.wiping,
                                    })}
                                    style={{ backgroundImage: `url(${wipeOverlay.dataUrl})` }}
                                    onAnimationEnd={handleWipeAnimationEnd}
                                    aria-hidden
                                />
                            )}
                            {hasDrawing && !hideDrawing && editorIsReady && (
                                <div className="drawing-svg-container">
                                    <div className="drawing-wrapper" style={svgWrapperStyle}>
                                        <SimpleDrawingSvg element={element} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <Caption
                        element={element}
                        textContent={getCaption(element)}
                        captionVisible={getShowCaption(element)}
                    />
                    {showWarningDialog && (
                        <div className="warning-dialog">
                            <div className="dialog-body">
                                <div className="dialog-text">
                                    <span className="warning-label">{translate('warningLabel')}</span>
                                    &nbsp;
                                    {translate('warningMessage')}
                                </div>

                                <div className="buttons">
                                    <Button className="StyledButton secondary" onClickFn={handleCancelRotation}>
                                        {translate('cancel')}
                                    </Button>
                                    <Button className="StyledButton danger" onClickFn={handleConfirmRotation}>
                                        {translate('continueAnyway')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {toast}
            <ImageModalToolbarSecondaryTools className={toolbarClassName}>
                {isV2Toolbar ? (
                    toolbarMode === 'background' ? (
                        <ImageModalV2BackgroundToolbar
                            element={element}
                            cropSourceImage={cropSourceImage}
                            largestImageUrl={largestUneditedImageUrl}
                            imageType={imageType}
                            progress={backgroundRemovalProgress}
                            setProgress={setBackgroundRemovalProgress}
                            disabled={toolbarDisabled}
                            stagedActive={backgroundRemovalStagedActive}
                            onChangeStagedActive={onChangeBackgroundRemovalStagedActive}
                            onCancelClick={cancelEditing}
                            onSaveClick={handleSave}
                        />
                    ) : (
                        <ImageModalV2EditorToolbar
                            cropActive={isCropToolSelected}
                            disabled={toolbarDisabled}
                            onCropClick={toggleCropTool}
                            onRotateClick={handleRotateClick}
                            onCancelClick={cancelEditing}
                            onSaveClick={handleSave}
                        />
                    )
                ) : (
                    <>
                        <Button
                            className="StyledButton secondary image-control rotate"
                            onClickFn={handleRotateClick}
                            disabled={toolbarDisabled}
                        >
                            <Icon name="modal-toolbar-rotate" />
                            {translate('rotateLeft')}
                        </Button>

                        <Button className="StyledButton secondary cancel no-hover" onClickFn={cancelEditing}>
                            {translate('cancel')}
                        </Button>

                        <Button className="StyledButton primary save" onClickFn={handleSave} disabled={toolbarDisabled}>
                            {translate('save')}
                        </Button>
                    </>
                )}
            </ImageModalToolbarSecondaryTools>
        </>
    );
};

const ImageModalEditor: React.FC<ImageModalEditorProps> = (props) => {
    const [editorIsReady, setEditorIsReady] = useState(false);
    const isV2Toolbar = props.toolbarVariant === 'v2';

    const toolbarClassName = classNames('ImageEditorModalToolbar', {
        'ImageEditorModalToolbar-v2': isV2Toolbar,
    });

    return (
        <ImageModalEditorContent
            {...props}
            editorIsReady={editorIsReady}
            isV2Toolbar={isV2Toolbar}
            showImageReplacementWipe={false}
            showEditorLoadingPreview={!editorIsReady}
            toolbarClassName={toolbarClassName}
            backgroundRemovalProgress={null}
            backgroundRemovalStagedActive={isBackgroundRemovalActive(props.element)}
            onChangeBackgroundRemovalStagedActive={() => undefined}
            onEditorReadyChange={setEditorIsReady}
            setBackgroundRemovalProgress={() => undefined}
        />
    );
};

export default ImageModalEditor;
